import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, Users, UserPlus, Zap, Clock, TrendingUp,
  CheckCircle, Fingerprint, Heart, BarChart3,
} from "lucide-react";
import { API } from "../App";

// ---------- Helpers ----------
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "—"; }
};
const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "gerade eben";
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  if (diff < 86400 * 7) return `vor ${Math.floor(diff / 86400)} Tagen`;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
};

// ---------- Sub-Components ----------
const StatCard = ({ icon: Icon, label, value, sub, accent = "emerald", testId }) => {
  const accents = {
    emerald: "from-emerald-500 to-teal-600",
    violet: "from-violet-500 to-purple-600",
    amber: "from-amber-500 to-orange-500",
    blue: "from-blue-500 to-cyan-500",
    rose: "from-rose-500 to-pink-500",
    slate: "from-slate-700 to-slate-900",
  };
  return (
    <div data-testid={testId} className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden">
      <div className={`h-1 bg-gradient-to-r ${accents[accent] || accents.emerald}`} />
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <Icon className="w-5 h-5 text-slate-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

const UserRow = ({ u, meta }) => (
  <div className="flex items-center justify-between py-3 px-4 hover:bg-slate-50 transition-colors">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
        {(u.username || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0">
        <p className="font-semibold text-slate-800 truncate">{u.username}</p>
        <p className="text-xs text-slate-500 truncate">{u.email}</p>
      </div>
    </div>
    <div className="text-right flex-shrink-0 ml-3">{meta}</div>
  </div>
);

const MiniBar = ({ data, selectedIdx, onSelect }) => {
  const max = Math.max(1, ...data.map(d => d.count));
  return (
    <div className="flex items-end gap-1.5 h-24">
      {data.map((d, i) => {
        const h = Math.round((d.count / max) * 100);
        const isActive = i === selectedIdx;
        const clickable = d.count > 0;
        return (
          <button
            type="button"
            key={i}
            data-testid={`trend-bar-${i}`}
            onClick={() => clickable && onSelect(isActive ? null : i)}
            disabled={!clickable}
            className={`flex-1 flex flex-col items-center gap-1 group rounded-md transition-all ${
              clickable ? "cursor-pointer hover:bg-slate-50" : "cursor-default opacity-60"
            } ${isActive ? "ring-2 ring-emerald-500 ring-offset-2 bg-emerald-50" : ""}`}
            title={clickable ? `${d.range_label || d.label}: ${d.count} neue User – klicken für Details` : `${d.label}: 0 neue User`}
          >
            <div className="w-full relative flex-1 flex items-end px-0.5 pt-4">
              <div
                className={`w-full rounded-t-md transition-all ${
                  isActive
                    ? "bg-gradient-to-t from-emerald-700 to-emerald-500"
                    : "bg-gradient-to-t from-emerald-600 to-emerald-400 group-hover:from-emerald-700 group-hover:to-emerald-500"
                }`}
                style={{ height: `${h}%`, minHeight: d.count > 0 ? "4px" : "1px" }}
              />
              {d.count > 0 && (
                <span className={`absolute top-0 left-1/2 -translate-x-1/2 text-[10px] font-bold ${
                  isActive ? "text-emerald-700" : "text-slate-600 opacity-0 group-hover:opacity-100"
                }`}>
                  {d.count}
                </span>
              )}
            </div>
            <span className="text-[10px] text-slate-500">{d.label}</span>
          </button>
        );
      })}
    </div>
  );
};

// ---------- Main ----------
export default function AdminOverview() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeekIdx, setSelectedWeekIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/overview`);
        setData(res.data);
      } catch (err) {
        if (err?.response?.status === 403) {
          toast.error("Kein Zugriff auf das Admin-Panel.");
          navigate("/main-hub");
        } else {
          toast.error("Admin-Daten konnten nicht geladen werden.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Lade Admin-Daten…</div>
      </div>
    );
  }
  if (!data) return null;

  const { stats, new_users_7, most_active, last_online, registration_trend } = data;
  const pct = (n, total) => total > 0 ? Math.round((n / total) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/main-hub">
            <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-white/5 mb-4" data-testid="admin-back">
              <ArrowLeft className="w-4 h-4 mr-2" /> Zurück zu Mein Business
            </Button>
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-amber-400" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Admin-Panel</p>
              <h1 className="text-3xl font-bold text-white">App-Nutzungs-Übersicht</h1>
              <p className="text-slate-400 text-sm mt-1">
                Stand: {new Date(data.generated_at).toLocaleString("de-DE")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Gesamt-Statistik Cards */}
        <section>
          <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-slate-600" /> Gesamt-Statistik
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard testId="stat-total-users" icon={Users} label="Registrierte User" value={stats.total_users} accent="emerald" />
            <StatCard testId="stat-verified" icon={CheckCircle} label="Verifiziert" value={stats.verified_users}
              sub={`${pct(stats.verified_users, stats.total_users)}% aller User`} accent="blue" />
            <StatCard testId="stat-biometric" icon={Fingerprint} label="Biometrie aktiv" value={stats.biometric_count}
              sub={`${pct(stats.biometric_count, stats.total_users)}% der User`} accent="violet" />
            <StatCard testId="stat-donors" icon={Heart} label="Unterstützer" value={stats.donor_count} accent="rose" />
            <StatCard testId="stat-active-7" icon={Zap} label="Aktiv letzte 7 Tage" value={stats.active_last_7_days}
              sub={`${pct(stats.active_last_7_days, stats.total_users)}% aller User`} accent="amber" />
            <StatCard testId="stat-active-30" icon={Clock} label="Aktiv letzte 30 Tage" value={stats.active_last_30_days}
              sub={`${pct(stats.active_last_30_days, stats.total_users)}% aller User`} accent="amber" />
            <StatCard testId="stat-total-logins" icon={TrendingUp} label="Gesamt-Logins" value={stats.total_logins} accent="slate" />
            <StatCard testId="stat-avg-logins" icon={TrendingUp} label="Ø Logins / User" value={stats.avg_logins_per_user} accent="slate" />
          </div>
        </section>

        {/* Registrierungs-Trend */}
        <section className="bg-white rounded-2xl shadow-md border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-slate-600" /> Neuanmeldungen (letzte 8 Wochen)
          </h2>
          <p className="text-xs text-slate-500 mb-5">
            {stats.new_users_last_7_days} neue in den letzten 7 Tagen · {stats.new_users_last_30_days} in den letzten 30 Tagen
            {" · "}<span className="text-emerald-700 font-medium">Klick auf einen Balken für Details</span>
          </p>
          <MiniBar
            data={registration_trend}
            selectedIdx={selectedWeekIdx}
            onSelect={setSelectedWeekIdx}
          />

          {selectedWeekIdx !== null && registration_trend[selectedWeekIdx] && (
            <div className="mt-5 border-t border-slate-200 pt-5" data-testid="trend-detail-panel">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    {registration_trend[selectedWeekIdx].count} neue User
                    <span className="text-slate-500 font-normal ml-2">
                      · {registration_trend[selectedWeekIdx].range_label || registration_trend[selectedWeekIdx].label}
                    </span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedWeekIdx(null)}
                  className="text-xs text-slate-500 hover:text-slate-700"
                  data-testid="trend-detail-close"
                >
                  ✕ Schließen
                </button>
              </div>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
                {(registration_trend[selectedWeekIdx].users || []).map((u) => {
                  const lastSeen = u.last_active_at || u.last_login_at;
                  return (
                    <div key={u.id} className="flex items-center justify-between py-3 px-4 hover:bg-slate-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                          {(u.username || "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{u.username}</p>
                          <p className="text-xs text-slate-500 truncate">{u.email}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            registriert: {formatDate(u.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3 flex items-center gap-4">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-slate-400">Zuletzt online</p>
                          <p className="text-xs font-semibold text-slate-700">
                            {lastSeen ? formatRelative(lastSeen) : "noch nie"}
                          </p>
                          {lastSeen && (
                            <p className="text-[10px] text-slate-400">{formatDate(lastSeen)}</p>
                          )}
                        </div>
                        <div className="border-l border-slate-200 pl-4">
                          <p className="text-[10px] uppercase tracking-wider text-slate-400">Logins</p>
                          <p className="text-sm font-bold text-slate-800">{u.login_count}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {(registration_trend[selectedWeekIdx].users || []).length === 0 && (
                  <p className="p-4 text-sm text-slate-500">Keine User in dieser Woche.</p>
                )}
              </div>
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Zuletzt Online */}
          <section className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden" data-testid="section-last-online">
            <div className="p-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-600" /> Zuletzt online (Top 5)
              </h2>
              <p className="text-xs text-slate-500 mt-1">Basierend auf API-Aktivität (throttled ~5 Min)</p>
            </div>
            <div className="divide-y divide-slate-100">
              {last_online.length === 0 && (
                <p className="p-5 text-sm text-slate-500">Noch keine Online-Daten vorhanden.</p>
              )}
              {last_online.map((u) => (
                <UserRow
                  key={u.id}
                  u={u}
                  meta={
                    <div>
                      <p className="text-xs font-semibold text-emerald-600">{formatRelative(u.last_active_at || u.last_login_at)}</p>
                      <p className="text-[10px] text-slate-400">{formatDate(u.last_active_at || u.last_login_at)}</p>
                    </div>
                  }
                />
              ))}
            </div>
          </section>

          {/* Most Active */}
          <section className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden" data-testid="section-most-active">
            <div className="p-5 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" /> Aktivste User (Top 10)
              </h2>
              <p className="text-xs text-slate-500 mt-1">Sortiert nach Anzahl erfolgreicher Logins</p>
            </div>
            <div className="divide-y divide-slate-100">
              {most_active.map((u, i) => (
                <UserRow
                  key={u.id}
                  u={u}
                  meta={
                    <div className="flex items-center gap-2">
                      {i < 3 && (
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          i === 0 ? "bg-amber-100 text-amber-700"
                          : i === 1 ? "bg-slate-200 text-slate-700"
                          : "bg-orange-100 text-orange-700"
                        }`}>#{i + 1}</span>
                      )}
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-800">{u.login_count}</p>
                        <p className="text-[10px] text-slate-500">Logins</p>
                      </div>
                    </div>
                  }
                />
              ))}
            </div>
          </section>
        </div>

        {/* New Users */}
        <section className="bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden" data-testid="section-new-users">
          <div className="p-5 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" /> Neu dazugekommen (letzte 7 Tage)
            </h2>
            <p className="text-xs text-slate-500 mt-1">{new_users_7.length} neue Registrierungen</p>
          </div>
          <div className="divide-y divide-slate-100">
            {new_users_7.length === 0 && (
              <p className="p-5 text-sm text-slate-500">Keine Neuanmeldungen in den letzten 7 Tagen.</p>
            )}
            {new_users_7.map((u) => (
              <UserRow
                key={u.id}
                u={u}
                meta={
                  <div>
                    <p className="text-xs font-semibold text-emerald-600">{formatRelative(u.created_at)}</p>
                    <p className="text-[10px] text-slate-400">{formatDate(u.created_at)}</p>
                  </div>
                }
              />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
